import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Card,
  CardMedia,
  CircularProgress,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { useApi } from "../../hooks";
import { apiGallery, method } from "../../apiPath";
import keyNames from "../../keyName";
import { useSelector } from "react-redux";
import { PageHeader } from "../../component";
import { useNavigate } from "react-router-dom";

const { get } = method;

const Gallery = () => {
  const hitApi = useApi();
  const storeData = useSelector((state) => state.mainReducer);
  const { galleryData } = storeData;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true); // initial loading
  const [path, setPath] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  useEffect(() => {
    const fetchIfNeeded = async () => {
      if (!galleryData) {
        await hitApi(apiGallery, null, get, keyNames.galleryData);
      }
      setLoading(false);
    };
    fetchIfNeeded();
  }, [galleryData, hitApi]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!galleryData) return <Typography>No data found</Typography>;

  const folders = Object.entries(galleryData || {}).reduce(
    (acc, [key, value]) => {
      if (!Array.isArray(value)) return acc;
      const prefix = key.split("_")[0];
      if (!acc[prefix]) acc[prefix] = {};
      acc[prefix][key] = value;
      return acc;
    },
    {}
  );

  const goBack = () => {
    setLoading(true);
    setTimeout(() => {
      setPath((prev) => prev.slice(0, -1));
      setLoading(false);
    }, 300); // small delay to simulate loading
  };

  const handleNavigate = (newPath) => {
    setLoading(true);
    setTimeout(() => {
      setPath(newPath);
      setLoading(false);
    }, 300); // simulate loading
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setSnackbar({ open: true, message: "Image URL copied!" });
    });
  };

  let content;
  if (path.length === 0) {
    content = (
      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 3 }}
      >
        {Object.keys(folders).map((main) => (
          <Box
            key={main}
            onClick={() => handleNavigate([main])}
            sx={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              bgcolor: "#f5f5f5",
              boxShadow: 1,
              gap: "20px",
              p: 1,
              borderRadius: 1,
              "&:hover": { opacity: 0.7 },
            }}
          >
            <FolderIcon sx={{ fontSize: 50, color: "#fbc02d" }} />
            <Typography variant="body1">{main}</Typography>
          </Box>
        ))}
      </Box>
    );
  } else if (path.length === 1) {
    const subFolders = folders[path[0]];
    content = (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 3,
        }}
      >
        {Object.keys(subFolders).map((sub) => (
          <Box
            key={sub}
            onClick={() => handleNavigate([path[0], sub])}
            sx={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "row",
              gap: "20px",
              bgcolor: "#f5f5f5",
              boxShadow: 1,
              p: 1,
              borderRadius: 1,
              alignItems: "center",
              "&:hover": { opacity: 0.7 },
            }}
          >
            <FolderIcon sx={{ fontSize: 60, color: "#fbc02d" }} />
            <Typography variant="body1">{sub}</Typography>
          </Box>
        ))}
      </Box>
    );
  } else if (path.length === 2) {
    const images = folders[path[0]][path[1]];
    content =
      images.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)", // 4 per row for images
            gap: 2,
          }}
        >
          {images.map((img, idx) => (
            <Card
              key={idx}
              sx={{
                borderRadius: 2,
                boxShadow: 3,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image={img.fileSrc}
                alt={img.originalName}
                sx={{ objectFit: "cover" }}
              />
              <IconButton
                size="small"
                onClick={() => handleCopy(img.fileSrc)}
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  background: "rgba(255,255,255,0.7)",
                  "&:hover": { background: "rgba(255,255,255,1)" },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Card>
          ))}
        </Box>
      ) : (
        <Typography>No images available</Typography>
      );
  }

  const btn = {
    show: false,
    type: "contained",
  };

  return (
    <>
      <PageHeader title={"Gallery"} btn={btn} />
      <Box sx={{ p: 2 }}>
        {path.length > 0 && (
          <IconButton onClick={goBack} sx={{ mb: 2 }}>
            <ArrowBackIcon />
          </IconButton>
        )}

        <Typography variant="subtitle2" sx={{ mb: 2, color: "gray" }}>
          {path.length === 0 ? "Root" : path.join(" / ")}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          content
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={2000}
          onClose={() => setSnackbar({ open: false, message: "" })}
          message={snackbar.message}
        />
      </Box>
    </>
  );
};

export default Gallery;
